import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import MainLayout from "@/components/layout/MainLayout";
import { GradientPageHeader } from "@/components/layout/GradientPageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputSubmit,
  PromptInputToolbar,
  PromptInputModelSelect,
  PromptInputModelSelectTrigger,
  PromptInputModelSelectContent,
  PromptInputModelSelectItem,
  PromptInputModelSelectValue,
} from "@/components/ai-elements/prompt-input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AIAssistantForm } from "@/components/super-admin/AIAssistantForm";
import { AIAssistantsSection } from "@/components/agents-list";
import { aiAssistantService } from "@/services/aiAssistantService";
import { chatThreadsService } from "@/services/chatThreadsService";
import { featureFlagService } from "@/services/featureFlags";
import { Bot, Wand2, MessageSquare, Search, Plus } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import AIAssistantCreationModal from "@/components/ai-assistant/AIAssistantCreationModal";
import { SmartIcon } from "@/components/ui/SmartIcon";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const AIAssistantsPage = () => {
  const { user, organization, isAdmin } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const onSelect = () => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    };

    onSelect();
    carouselApi.on("reInit", onSelect);
    carouselApi.on("select", onSelect);

    return () => {
      carouselApi?.off("select", onSelect);
    };
  }, [carouselApi]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isAICreationOpen, setIsAICreationOpen] = useState(false);
  const [makePublic, setMakePublic] = useState(false);
  const [selectedChatAssistantId, setSelectedChatAssistantId] = useState<
    string | null
  >(null);
  const [masterPrompt, setMasterPrompt] = useState<string>("");
  const [isBootstrappingChat, setIsBootstrappingChat] = useState(false);
  const rotatingSuggestions = React.useMemo(
    () => [
      "Plan a project",
      "Reply to an email",
      "Summarize a meeting",
      "Write a LinkedIn post",
      "Prep for a meeting",
      "Write a blog article",
    ],
    [],
  );
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  React.useEffect(() => {
    const id = setInterval(() => {
      setSuggestionIndex((i) => (i + 1) % rotatingSuggestions.length);
    }, 3000);
    return () => clearInterval(id);
  }, [rotatingSuggestions.length]);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");

  // Check if chat assistants feature is enabled
  const {
    data: chatAssistantsEnabled,
    isLoading: isLoadingChatFeature,
    error: chatFeatureError,
  } = useQuery({
    queryKey: ["chat-assistants-feature", organization?.id],
    queryFn: async () => {
      if (!organization?.id) return true; // Default to true if no org
      return await featureFlagService.isChatAssistantsEnabled(organization.id);
    },
    enabled: !!organization?.id,
    retry: 1,
    onError: (error) => {
      console.error("Failed to fetch chat assistants feature flag:", error);
    },
  });

  // Fallback to true if error or undefined (backward compatible)
  const shouldShowChatAssistants = chatAssistantsEnabled ?? true;

  // Fetch all system AI assistants (for discovery)
  const {
    data: systemAssistantsResponse,
    isLoading: isLoadingAIAssistants,
    error: aiAssistantsError,
  } = useQuery({
    queryKey: ["system-ai-assistants"],
    queryFn: () => aiAssistantService.getSystemAssistants(),
    enabled: !!user && !!organization?.id,
  });

  // Extract and scope assistants: global (owner null) + org-owned
  const systemAssistants = systemAssistantsResponse?.data || [];
  const availableAssistants = useMemo(() => {
    if (!organization?.id) return systemAssistants;
    return systemAssistants.filter(
      (a: any) =>
        a?.owner_organization_id === null ||
        a?.owner_organization_id === undefined ||
        a?.owner_organization_id === organization.id,
    );
  }, [systemAssistants, organization?.id]);

  // Get unique categories from assistants
  const categories = useMemo(() => {
    const unique = Array.from(
      new Set(availableAssistants.map((a) => a.category)),
    );
    const desiredOrder = [
      "Marketing",
      "Sales",
      "Daily work",
      "Customer Engagement",
      "Operations",
      "Finance",
      "HR",
      "Legal",
    ];
    const ordered = desiredOrder.filter((c) => unique.includes(c));
    const remaining = unique.filter((c) => !desiredOrder.includes(c)).sort();
    return ["All", ...ordered, ...remaining];
  }, [availableAssistants]);

  // Separate chat assistants from generation assistants
  const chatAssistants = useMemo(() => {
    return availableAssistants.filter((a: any) => a.supports_chat === true);
  }, [availableAssistants]);

  const generationAssistants = useMemo(() => {
    return availableAssistants.filter((a: any) => a.supports_chat !== true);
  }, [availableAssistants]);

  // Filter assistants based on selected category and search query
  const filteredAssistants = useMemo(() => {
    let filtered = generationAssistants;

    // Filter by category
    if (selectedCategory !== "All") {
      filtered = filtered.filter(
        (assistant) => assistant.category === selectedCategory,
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (assistant) =>
          assistant.title.toLowerCase().includes(query) ||
          assistant.description.toLowerCase().includes(query) ||
          assistant.name.toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [generationAssistants, selectedCategory, searchQuery]);

  // Filter chat assistants for search and sort A→Z by description text
  const filteredChatAssistants = useMemo(() => {
    const list = !searchQuery.trim()
      ? chatAssistants
      : chatAssistants.filter((assistant: any) => {
          const query = searchQuery.toLowerCase().trim();
          return (
            assistant.title?.toLowerCase().includes(query) ||
            assistant.description?.toLowerCase().includes(query) ||
            assistant.name?.toLowerCase().includes(query)
          );
        });

    return [...list].sort((a: any, b: any) => {
      const da = (a.description || "").toString().trim().toLowerCase();
      const db = (b.description || "").toString().trim().toLowerCase();
      if (da < db) return -1;
      if (da > db) return 1;
      return 0;
    });
  }, [chatAssistants, searchQuery]);

  // Start chat from master input
  const startChatWithPrompt = async () => {
    if (!organization?.id) {
      toast({
        title: "Error",
        description: "Organization not found",
        variant: "destructive",
      });
      return;
    }
    if (!selectedChatAssistantId) {
      toast({
        title: "Select an AI assistant",
        description: "Choose an AI assistant, then type your question.",
      });
      return;
    }
    const text = masterPrompt.trim();
    if (!text) {
      toast({
        title: "Type a message",
        description: "Enter what you want to ask before sending.",
      });
      return;
    }

    try {
      setIsBootstrappingChat(true);
      const result = await aiAssistantService.findOrCreateAssistantInstance(
        selectedChatAssistantId,
        organization.id,
      );
      if (!result?.data?.id)
        throw new Error("Failed to create or find assistant instance");
      const instanceId = result.data.id;

      // Always create a fresh thread for master input usage
      const { data: thread, error } = await chatThreadsService.createThread(
        organization.id,
        instanceId,
        "New Chat",
        user?.id || undefined,
      );
      if (error || !thread?.id)
        throw error || new Error("Failed to create thread");

      // Navigate carrying threadId and initial prompt via route state
      navigate(`/chat-assistants/${instanceId}`, {
        state: { initialPrompt: text, initialThreadId: thread.id },
      });
    } catch (e: any) {
      toast({
        title: "Failed to start chat",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsBootstrappingChat(false);
    }
  };

  if (aiAssistantsError) {
    console.error("Error loading AI assistants:", aiAssistantsError);
    toast({
      title: "Error",
      description:
        "Failed to load AI assistants. Please try refreshing the page.",
      variant: "destructive",
    });
  }

  // Create user assistant then create instance and navigate
  const createAssistantMutation = useMutation({
    mutationFn: async (formData: any) => {
      if (!organization?.id) throw new Error("Organization not found");
      // Map form data to DB shape; allow optional public toggle
      const { is_public, ...rest } = formData;
      const result = await aiAssistantService.createUserAssistant(
        organization.id,
        { ...rest, is_public: !!is_public },
      );
      if (result.error || !result.data)
        throw result.error || new Error("Create assistant failed");
      return result.data;
    },
    onSuccess: async (assistant) => {
      try {
        // Find or create instance for this org
        const { data: instance, error } =
          await aiAssistantService.findOrCreateAssistantInstance(
            assistant.id,
            organization!.id,
            assistant.name,
          );
        if (error || !instance)
          throw error || new Error("Failed to create instance");
        // Refresh listings and navigate
        queryClient.invalidateQueries({ queryKey: ["system-ai-assistants"] });
        setIsCreateOpen(false);
        toast({
          title: "Assistant created",
          description: "You can start using it now.",
        });
        navigate(`/ai-assistants/${instance.id}`);
      } catch (e: any) {
        toast({
          title: "Setup failed",
          description: e?.message || "Could not set up instance",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Creation failed",
        description: error?.message || "Could not create assistant",
        variant: "destructive",
      });
    },
  });

  // Handle opening chat assistant
  const handleOpenChat = async (assistantId: string) => {
    if (!organization?.id) {
      toast({
        title: "Error",
        description: "Organization not found",
        variant: "destructive",
      });
      return;
    }

    try {
      const res = await aiAssistantService.findOrCreateAssistantInstance(
        assistantId,
        organization.id,
      );

      if (res.data?.id) {
        navigate(`/chat-assistants/${res.data.id}`);
      } else {
        throw new Error("Failed to create instance");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to open chat",
        variant: "destructive",
      });
    }
  };

  return (
    <MainLayout>
      <div className="flex flex-col min-h-full">
        <GradientPageHeader
          title="What do you need help with today?"
          titleHighlight="today"
          description="Search for an AI tool to get started"
          rightContent={
            <Button
              variant="ghost"
              onClick={() => setIsAICreationOpen(true)}
              className="flex items-center gap-2 bg-transparent border-0 text-gray-600 hover:text-gray-800 hover:bg-transparent"
            >
              <Plus className="h-4 w-4" />
              Create AI tool
            </Button>
          }
          belowDescription={
            <div className="max-w-[44rem] mx-auto relative">
              <style
                dangerouslySetInnerHTML={{
                  __html: `@keyframes borderGlow { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }`,
                }}
              />
              {/* Search Input for filtering AI tools */}
              <div
                className="relative rounded-xl mb-4 mx-auto"
                style={{
                  border: "2px solid transparent",
                  background:
                    "linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(90deg, rgb(158, 172, 211), rgb(234, 179, 8), rgba(255,255,255,0.95), rgb(158, 172, 211), rgba(255,255,255,0.95)) border-box",
                  backgroundSize: "400% 100%",
                  animation: "borderGlow 8s linear infinite",
                  maxWidth: "450px",
                }}
              >
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />
                <Input
                  type="text"
                  placeholder="Search AI tools..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 bg-white border-none rounded-xl h-14 text-base"
                  style={
                    {
                      "--tw-ring-color": "rgb(158, 172, 211)",
                    } as React.CSSProperties
                  }
                />
              </div>

              {/* Category Filters - Centered */}
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center">
                  {categories.map((category) => (
                    <Button
                      key={category}
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedCategory(category)}
                      className={
                        selectedCategory === category
                          ? "border-2 border-gray-400 bg-gray-50 text-gray-700 font-medium"
                          : "border border-gray-200 text-gray-600 hover:border-gray-300"
                      }
                    >
                      {category}
                    </Button>
                  ))}
                </div>
              )}

              {/* CHAT INPUT - TEMPORARILY HIDDEN - May enable later */}
              {/* {isAdmin && (
                <div className="relative rounded-xl" style={{ border: "2px solid transparent", background: "linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(90deg, rgb(158, 172, 211), rgb(234, 179, 8), rgba(255,255,255,0.95), rgb(158, 172, 211), rgba(255,255,255,0.95)) border-box", backgroundSize: "400% 100%", animation: "borderGlow 8s linear infinite" }}>
                  <PromptInput onSubmit={(e) => { e.preventDefault(); startChatWithPrompt(); }} className="relative rounded-xl bg-white">
                    <PromptInputTextarea value={masterPrompt} onChange={(e) => setMasterPrompt(e.target.value)} placeholder={rotatingSuggestions[suggestionIndex]} className="min-h-[56px] border-none" autoFocus />
                    <PromptInputToolbar>
                      <div className="flex items-center gap-2">
                        <PromptInputModelSelect value={selectedChatAssistantId || undefined} onValueChange={(v) => setSelectedChatAssistantId(v)}>
                          <PromptInputModelSelectTrigger>
                            <div className="flex items-center gap-2 text-gray-600">
                              <Wand2 className="h-4 w-4" />
                              <PromptInputModelSelectValue placeholder="Select an assistant" />
                            </div>
                          </PromptInputModelSelectTrigger>
                          <PromptInputModelSelectContent className="min-w-[280px]">
                            {chatAssistants.map((a: any) => (
                              <PromptInputModelSelectItem key={a.id} value={a.id}>
                                <span className="flex items-center gap-2">
                                  <SmartIcon iconUrl={a.icon_url} size={18} className="shrink-0 rounded-sm" alt={`${a.title || a.name} icon`} />
                                  <span>{(a.name || "Untitled").trim()} {a.title ? `- ${a.title}` : ""}</span>
                                </span>
                              </PromptInputModelSelectItem>
                            ))}
                          </PromptInputModelSelectContent>
                        </PromptInputModelSelect>
                      </div>
                      <div className="flex items-center gap-1">
                        <PromptInputSubmit disabled={!masterPrompt.trim() || !selectedChatAssistantId || isBootstrappingChat} />
                      </div>
                    </PromptInputToolbar>
                  </PromptInput>
                </div>
              )} */}
            </div>
          }
        />

        {/* Page content now scrolls normally; header is not blocking */}

        <div className="flex-1 -mt-4">
          <div className="container mx-auto px-6 py-2 w-full max-w-[68rem]">
            {/* AI Assistants Section - Only show if feature is enabled */}
            {isAdmin &&
              !isLoadingChatFeature &&
              shouldShowChatAssistants &&
              filteredChatAssistants.length > 0 && (
                <div className="mb-3">
                  <Carousel
                    setApi={setCarouselApi}
                    opts={{
                      align: "center",
                      loop: false,
                    }}
                    className="w-full"
                  >
                    <CarouselContent className="justify-center">
                      {filteredChatAssistants.map((assistant: any) => (
                        <CarouselItem
                          key={assistant.id}
                          className="pl-2 md:pl-4 basis-auto"
                        >
                          <button
                            type="button"
                            onClick={() => handleOpenChat(assistant.id)}
                            className="flex flex-col items-center gap-3 group w-32 p-2"
                            title={assistant.title}
                          >
                            <div className="relative w-16 h-16 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm transition-all duration-500 ease-out group-hover:shadow-2xl group-hover:shadow-blue-500/25 group-hover:scale-110 group-hover:border-transparent overflow-visible">
                              {/* Futuristic ring effects - only on hover */}
                              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400/20 via-purple-400/20 to-cyan-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 group-hover:animate-pulse"></div>
                              <div className="absolute inset-0 rounded-full border-2 border-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-border opacity-0 group-hover:opacity-60 transition-opacity duration-500"></div>
                              <div className="absolute inset-1 rounded-full bg-white group-hover:bg-gradient-to-br group-hover:from-blue-50 group-hover:to-indigo-100 transition-all duration-500"></div>

                              {/* Icon container */}
                              <div className="relative z-10 transition-all duration-500 group-hover:drop-shadow-lg">
                                <SmartIcon
                                  iconUrl={assistant.icon_url}
                                  size={56}
                                  fallbackIcon={<Bot className="h-6 w-6" />}
                                  alt={`${assistant.title || "AI tool"} icon`}
                                  className="opacity-80"
                                />
                              </div>

                              {/* Animated particles - only on hover */}
                              <div className="absolute inset-0 rounded-full overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                <div className="absolute top-2 left-2 w-1 h-1 bg-blue-400 rounded-full animate-ping"></div>
                                <div
                                  className="absolute bottom-3 right-4 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping"
                                  style={{ animationDelay: "0.5s" }}
                                ></div>
                                <div
                                  className="absolute top-4 right-2 w-1 h-1 bg-cyan-400 rounded-full animate-ping"
                                  style={{ animationDelay: "1s" }}
                                ></div>
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-sm font-semibold text-slate-800 leading-tight group-hover:text-blue-700 transition-colors duration-300">
                                {assistant.title}
                              </div>
                              <div className="text-xs text-gray-500">
                                {assistant.name}
                              </div>
                            </div>
                          </button>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {canScrollPrev && <CarouselPrevious className="left-0" />}
                    {canScrollNext && <CarouselNext className="right-0" />}
                  </Carousel>
                </div>
              )}

            {/* Split into sections: custom, recommended for industry, Hubflo general, other */}
            {(() => {
              const orgId = organization?.id || null;
              const orgIndustryId = (organization as any)?.industry_id || null;
              const custom = filteredAssistants.filter(
                (a: any) => a.owner_organization_id === orgId,
              );
              const hubfloAll = filteredAssistants.filter(
                (a: any) =>
                  a.owner_organization_id === null ||
                  a.owner_organization_id === undefined,
              );
              // Hubflo general assistants: industry_id is null
              const hubfloGeneral = hubfloAll.filter(
                (a: any) => !a.industry_id,
              );
              // Recommended: industry matches org
              const recommended = hubfloAll.filter(
                (a: any) => a.industry_id && a.industry_id === orgIndustryId,
              );
              // Group other industry assistants by industry
              const otherIndustryAssistants = hubfloAll.filter(
                (a: any) => a.industry_id && a.industry_id !== orgIndustryId,
              );

              // Group by industry for better organization
              const groupedByIndustry = otherIndustryAssistants.reduce(
                (acc: any, assistant: any) => {
                  const industry = assistant.industries;
                  if (industry) {
                    const key = industry.id;
                    if (!acc[key]) {
                      acc[key] = {
                        industry: industry,
                        assistants: [],
                      };
                    }
                    acc[key].assistants.push(assistant);
                  }
                  return acc;
                },
                {},
              );

              const industryName = (organization as any)?.industries?.name;
              return (
                <div className="space-y-8">
                  {custom.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">
                        My custom AI tools
                      </h3>
                      <AIAssistantsSection
                        assistants={custom}
                        isLoading={isLoadingAIAssistants}
                        organizationId={orgId}
                        searchQuery={searchQuery}
                        selectedCategory={selectedCategory}
                        hideIndustryTags={true}
                      />
                    </div>
                  )}
                  {recommended.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3">
                        Recommended for {industryName || "your industry"}
                      </h3>
                      <AIAssistantsSection
                        assistants={recommended}
                        isLoading={isLoadingAIAssistants}
                        organizationId={orgId}
                        searchQuery={searchQuery}
                        selectedCategory={selectedCategory}
                        hideIndustryTags={true}
                      />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      General AI tools
                    </h3>
                    <AIAssistantsSection
                      assistants={hubfloGeneral}
                      isLoading={isLoadingAIAssistants}
                      organizationId={orgId}
                      searchQuery={searchQuery}
                      selectedCategory={selectedCategory}
                      hideIndustryTags={true}
                    />
                  </div>
                  {/* Grouped industry sections */}
                  {Object.values(groupedByIndustry).map((group: any) => (
                    <div key={group.industry.id}>
                      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        {group.industry.emoji && (
                          <span>{group.industry.emoji}</span>
                        )}
                        {group.industry.name} assistants
                      </h3>
                      <AIAssistantsSection
                        assistants={group.assistants}
                        isLoading={isLoadingAIAssistants}
                        organizationId={orgId}
                        searchQuery={searchQuery}
                        selectedCategory={selectedCategory}
                        hideIndustryTags={true}
                      />
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Create Assistant Sheet (full-height sidebar) */}
        <Sheet open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <SheetContent className="w-[70%] max-w-none p-0 overflow-hidden">
            <div className="flex flex-col h-full">
              <SheetHeader className="p-6 pb-4 border-b">
                <SheetTitle>Create AI Tool</SheetTitle>
                <SheetDescription>
                  Define your AI tool. It will be private to your organization
                  by default.
                </SheetDescription>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto p-6">
                <AIAssistantForm
                  onSubmit={(data) =>
                    createAssistantMutation.mutate({
                      ...data,
                      is_public: makePublic,
                    })
                  }
                  onCancel={() => setIsCreateOpen(false)}
                  isLoading={createAssistantMutation.isPending}
                />
                <div className="mt-4 border-t pt-4 flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="make-public">Make public</Label>
                    <p className="text-xs text-gray-500">
                      Allow others to discover this AI tool. You can change this
                      later.
                    </p>
                  </div>
                  <Switch
                    id="make-public"
                    checked={makePublic}
                    onCheckedChange={setMakePublic}
                  />
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Create with AI Modal */}
        <AIAssistantCreationModal
          isOpen={isAICreationOpen}
          onClose={() => setIsAICreationOpen(false)}
        />
      </div>
    </MainLayout>
  );
};

export default AIAssistantsPage;
